'use strict';

const { getDependencyClass } = require('../../lib/dependencies.js');

const SPOTIM_EVENT = {
        apiReady: 'spot-im-api-ready',
        loginStart: 'spot-im-login-start',
        userLogout: 'spot-im-user-logout',
        renewSSO: 'spot-im-renew-sso',
    },
    SSO_LOGIN_HANDSHAKE_ENDPOINT = '/.api/social/open-web-sso-login-handshake',
    COMPONENT_CUSTOM_EVENT = 'phoenix:coral',
    COMPONENT_IN_VIEW = 'phoenix:owc:inview';

getDependencyClass('phoenix-page').then((pageClass) => {
    /**
     * OpenWebConversation is a Web Component responsible for loading a OpenWeb Conversation component.
     * For CLS reasons, we switched from lazy-loading upon visible to loading
     * as soon as the user starts scrolling or if the user lands anywhere other
     * than the top of the page.
     *
     * @example
     * <phoenix-open-web-conversation item-id="foo"><div data-spotim-module="conversation"
     * data-post-id="ci001" data-post-url="post-url"></div></phoenix-open-web-conversation>
     */
    class OpenWebConversation extends pageClass.PhoenixElement {
        get observedBroadcastedEvents() {
            return [COMPONENT_IN_VIEW];
        }

        connectedCallback() {
            return this.canRender().then((canRender) => {
                if (!canRender) {
                    return;
                }
                super.connectedCallback();
                document.addEventListener(SPOTIM_EVENT.loginStart, () => {
                    this._emitEvent('login');
                });
                this._logoutEventHandler = () => {
                    this._emitEvent('logout');
                };
                document.addEventListener(SPOTIM_EVENT.userLogout, this._logoutEventHandler);
                this.handleUserStatesListener = this._handleUserStates.bind(this);
                this._addListeners();
                return this.getCommentProfile().then((profile) => {
                    if (profile?.userId) {
                        this.profile = profile;
                        this.observeFirstIntersection();
                    } else {
                        this._OWLogout();
                    }
                });
            });
        }

        // Similar to our standard page `getKnownUser()` function, only hitting the
        // Phoenix wrapper for a lighter-weight Taiga endpoint that returns only
        // details required for OpenWeb, including the information passed in the SSO
        // handshake and subscription product info for sites that only allow subscriber
        // comments.
        getCommentProfile() {
            const self = this;
            function _getCommentsProfilePromise() {
                const rgisCookieVal = self.RGISCookieValue;
                if (!rgisCookieVal) {
                    return Promise.resolve({});
                }
                return fetch(
                    `/.api/memberships/comments-profile?rgis=${encodeURIComponent(rgisCookieVal)}`
                ).then((resp) => {
                    if (resp.status === 401) {
                        return {};
                    }
                    return resp.json();
                });
            }
            if (!window.phoenixCommentsProfilePromise) {
                window.phoenixCommentsProfilePromise = _getCommentsProfilePromise();
            }
            return window.phoenixCommentsProfilePromise;
        }

        _emitEvent(value) {
            this.emitEvent(COMPONENT_CUSTOM_EVENT, {
                bubbles: true,
                detail: {
                    value,
                },
            });
        }

        onFirstIntersection() {
            if (this.profile?.userId && this.profile?.displayName) {
                this._startOWSSO();
            }
        }

        _addListeners() {
            window.addEventListener(
                'log-event',
                this.handleUserStatesListener,
                pageClass.util.supportPassiveEventListeners() ? { passive: true } : false
            );
        }

        _startSSO() {
            const self = this;
            // We enter this function knowing by how it's called that we have window.SPOTIM, the user
            // is actively logged into our site, and the user has a displayName set. For pages such as
            // Real Money's "Daily Diary" where there are multiple openWebConversation instances with
            // different post IDs, we need to ensure that we call the Openweb handshake only once.
            const callback = function (codeA, completeSSOCallback) {
                // call our endpoint to receive codeB and return it to OpenWeb via completeSSOCallback()
                self.fetchWithBasePath(SSO_LOGIN_HANDSHAKE_ENDPOINT, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'x-rgis': self.RGISCookieValue,
                    },
                    body: JSON.stringify({
                        codeA,
                    }),
                })
                    .then((res) => {
                        if (!res.ok) {
                            return res.json().then((errorResponse) => {
                                throw new Error(`${res.status}: ${errorResponse.error}`);
                            });
                        }
                        return res.json();
                    })
                    .then((codeB) => {
                        if (codeB) {
                            completeSSOCallback(codeB);
                        }
                    })
                    .catch((err) => {
                        completeSSOCallback(null, err);
                    });
            };
            if (!window.phoenixOpenwebSSO) {
                const userId = this.profile?.userId;
                window.phoenixOpenwebSSO = window.SPOTIM.startSSO({ callback, userId })
                    .then(() => {
                        return true;
                    })
                    .catch((reason) => {
                        // eslint-disable-next-line no-console
                        console.error('SSO failed due to ', reason);
                        return false;
                    });
            }
            return window.phoenixOpenwebSSO;
        }

        _startOWSSO() {
            if (window.SPOTIM?.startSSO) {
                this._startSSO();
            } else {
                document.addEventListener(SPOTIM_EVENT.apiReady, this._startSSO.bind(this), false);
            }
            // In case of token expiration we need to renew sso
            document.addEventListener(SPOTIM_EVENT.renewSSO, this._startSSO.bind(this), false);
        }

        _OWLogout() {
            document.removeEventListener(SPOTIM_EVENT.userLogout, this._logoutEventHandler);
            if (window.SPOTIM?.logout) {
                window.SPOTIM.logout();
            } else {
                document.addEventListener(
                    SPOTIM_EVENT.apiReady,
                    function () {
                        window.SPOTIM.logout();
                    },
                    false
                );
            }
        }

        _removeListener() {
            window.removeEventListener('log-event', this.handleUserStatesListener);
        }

        _handleUserStates(e) {
            switch (e?.detail?.value) {
                case 'userLoginCompleted':
                    document.addEventListener(SPOTIM_EVENT.userLogout, this._logoutEventHandler);
                    this._startOWSSO();
                    break;
                case 'userLogoutCompleted':
                    this._OWLogout();
                    break;
            }
        }

        broadcastedEventCallback(name, event = {}) {
            const eventTarget = event?.detail?.value?.target;
            if (name === COMPONENT_IN_VIEW) {
                document.querySelector('html').style.scrollBehavior = 'smooth';
            }
        }

        disconnectedCallback() {
            super.disconnectedCallback();
            this._removeListener();
        }
    }
    window.customElements.define('phoenix-open-web-conversation', OpenWebConversation);
});
